import { useEffect, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Clock, Tag, CheckCircle } from 'lucide-react';
import { SEO, SchemaMarkup } from '../components/seo';
import { BrochureButton } from '../components/BrochureButton';
import type { ArticleBody } from '../lib/article-input';
import { fetchApiArticle, getMatchingPrerenderedArticle } from '../lib/articles-api';
import { ARTICLES, type Article } from '../lib/articles';

const ARTICLE_CONTENT: Record<string, ArticleBody> = {
  'or-en-vertrouwenspersoon-arbowet-raadplegingsplicht': {
    datePublished: '2026-09-29',
    dateModified: '2026-09-29',
    updatedLabel: 'september 2026',
    intro: 'Sinds 1 juli 2026 geldt een raadplegingsplicht over arbobeleid richting de OR, de personeelsvertegenwoordiging of, als die er niet zijn, de medewerkers zelf. Dat raakt direct de vraag wie de vertrouwenspersoon wordt. Dit artikel legt uit wat de plicht inhoudt, waarom de rol zich slecht verhoudt tot een OR- of HR-functie, en wat de OR kan agenderen.',
    sections: [
      {
        heading: 'Wat er sinds 1 juli 2026 is veranderd',
        body: [
          'De Arbowet verplicht werkgevers om medewerkers te raadplegen over het arbobeleid en de uitvoering daarvan. Die plicht geldt voor alle organisaties, ook zonder ondernemingsraad: is er geen OR of personeelsvertegenwoordiging, dan worden de betrokken medewerkers direct geraadpleegd.',
          'Raadplegen is meer dan de RI&E rondsturen of nieuwe regels aankondigen. Het vraagt om een gesprek waarin de OR of de medewerkers hun inbreng kunnen leveren voordat het besluit vastligt. De plicht vervangt het bestaande instemmingsrecht niet, maar voegt een stap toe aan de voorkant van de besluitvorming.',
        ],
      },
      {
        heading: 'Waarom dit de vertrouwenspersoon raakt',
        body: [
          'Het aanstellen van een vertrouwenspersoon valt onder het beleid rond psychosociale arbeidsbelasting en behoort daarmee tot de onderwerpen waarover overleg met de OR plaatsvindt. Voor de OR is dit het moment om niet alleen te kijken óf er een vertrouwenspersoon komt, maar ook wie het wordt en onder welke voorwaarden die persoon werkt.',
          'Een vertrouwenspersoon is op dit moment niet wettelijk verplicht. Dat betekent niet dat de keuze vrij is van consequenties: als de opvang na een melding niet functioneert, groeit het risico op escalatie en langdurige uitval.',
        ],
      },
      {
        heading: 'Waarom de rol niet bij een OR-lid of HR hoort',
        body: [
          'Zodra een bestuur overweegt de rol bij een OR-lid of een HR-medewerker te leggen, is dat voor de OR reden om aan de bel te trekken. De reden is belangenverstrengeling. Een HR-medewerker heeft een werkgeversrol: beoordelen, adviseren over arbeidsvoorwaarden, soms meebeslissen over een dossier. Een OR-lid heeft een politieke rol binnen de organisatie en is voor collega\'s zichtbaar verbonden aan medezeggenschap.',
          'De vertrouwenspersoon is juist het aanspreekpunt voor vertrouwelijke kwesties. Wie twijfelt of zijn verhaal daarna in een dossier terechtkomt, meldt niet. Dan gaat het probleem niet weg, het wordt alleen later en duurder zichtbaar.',
        ],
      },
      {
        heading: 'Wat de OR concreet kan agenderen',
        body: [
          'Vraag om de vertrouwenspersoon als expliciet onderdeel van het arbobeleid, met een beschrijving van positie, geheimhouding en jaarlijkse rapportage op geanonimiseerd niveau. Vraag ook naar bereikbaarheid: een naam op intranet zonder werkbare route is geen voorziening.',
          'Leg de raadpleging vast. Wie is wanneer geraadpleegd, wat is ingebracht en wat is met die inbreng gedaan. Dat is bruikbaar in het overleg, en het is het soort onderbouwing dat ook van pas komt als de Arbeidsinspectie het PSA-beleid controleert.',
        ],
      },
      {
        heading: 'De externe route',
        body: [
          'Een externe, LVV-gecertificeerde vertrouwenspersoon lost de belangenverstrengeling structureel op: geen arbeidsrelatie met collega\'s, geen rol in beoordelings- of personeelsdossiers. Voor MKB-organisaties en instellingen in zorg en onderwijs is dat vaak ook praktischer dan intern iemand vrijmaken en beschikbaar houden voor deze rol.',
        ],
      },
    ],
    summary: [
      'Sinds 1 juli 2026 moet de werkgever medewerkers raadplegen over arbobeleid: via de OR, de personeelsvertegenwoordiging of de medewerkers zelf.',
      'Het aanstellen van een vertrouwenspersoon valt onder het PSA-beleid en hoort daarmee in dat overleg thuis.',
      'Een OR-lid of HR-medewerker als vertrouwenspersoon leidt tot belangenverstrengeling en remt de meldbereidheid.',
      'De OR kan positie, geheimhouding, bereikbaarheid en jaarlijkse rapportage agenderen en de raadpleging laten vastleggen.',
      'Een externe, LVV-gecertificeerde vertrouwenspersoon lost de belangenverstrengeling structureel op.',
    ],
    cta: 'Wil je weten wat PuntUit voor jouw organisatie kan betekenen?',
  },

  'vertrouwenspersoon-verplicht': {
    datePublished: '2026-03-12',
    dateModified: '2026-06-30',
    updatedLabel: 'juni 2026',
    intro: 'Is een vertrouwenspersoon wettelijk verplicht? Het korte antwoord: nee, op dit moment niet. Maar de praktijk is genuanceerder. Er zijn meerdere wetten en regelingen die het aanstellen van een vertrouwenspersoon sterk aanmoedigen — en in sommige gevallen indirect afdwingen. In dit artikel leggen we de stand van zaken helder uit.',
    sections: [
      {
        heading: 'Het wetsvoorstel: aangenomen door de Tweede Kamer, maar nog niet van kracht',
        body: [
          'In mei 2023 is in de Tweede Kamer een wetsvoorstel aangenomen dat organisaties met meer dan 10 medewerkers verplicht zou stellen een vertrouwenspersoon aan te stellen. Sindsdien ligt het wetsvoorstel bij de Eerste Kamer.',
          'Door de veranderde samenstelling van de Eerste Kamer, de val van het kabinet-Rutte IV en de politieke ontwikkelingen daarna, is het wetsvoorstel op een zijspoor geraakt. Op dit moment is er geen concrete datum waarop de wet in werking treedt.',
        ],
      },
      {
        heading: 'De RI&E: vertrouwenspersoon als onderdeel van goed werkgeverschap',
        body: [
          'Hoewel een vertrouwenspersoon niet wettelijk verplicht is, komt het onderwerp wel terug in de Risico-Inventarisatie en Evaluatie (RI&E). In het deel over psychosociale arbeidsbelasting is er een apart kopje "Vertrouwenspersoon".',
          'Als de Arbeidsinspectie een controle uitvoert en constateert dat er geen vertrouwenspersoon is, kan zij op last van een boete alsnog verplichten er een aan te stellen. De kans op zo\'n controle is in de praktijk klein, maar het risico bestaat.',
        ],
      },
      {
        heading: 'De Wet bescherming klokkenluiders',
        body: [
          'De Wet bescherming klokkenluiders verplicht organisaties met 50 of meer werknemers tot een interne meldregeling. In die meldregeling wordt de vertrouwenspersoon vaak genoemd als aanspreekpunt voor opvang, advies en ondersteuning van medewerkers die misstanden willen melden.',
          'Strikt genomen is de vertrouwenspersoon als functionaris ook hierbij niet wettelijk verplicht. Maar in de praktijk is het lastig om aan de geest van de wet te voldoen zonder er een te hebben.',
        ],
      },
      {
        heading: 'Niet verplicht, wel verstandig',
        body: [
          'De juridische werkelijkheid is dat een vertrouwenspersoon op dit moment niet wettelijk verplicht is. Er is ook geen programma van eisen waaraan een vertrouwenspersoon moet voldoen.',
          'Maar de praktische werkelijkheid is anders. Ongewenst gedrag komt in elke organisatie voor. Zonder laagdrempelig meldpunt escaleert het. Eén geëscaleerde situatie kan leiden tot langdurig verzuim, juridische procedures en reputatieschade. De kosten daarvan overstijgen al snel tien jaar abonnement bij een externe vertrouwenspersoon.',
        ],
      },
      {
        heading: 'Wat kost het om het wél te regelen?',
        body: [
          'Bij PuntUit begint een compleet jaarabonnement vanaf €375 exclusief BTW per jaar — minder dan €40 per maand. Inclusief het PuntUit-spel, het gratis online spreekuur, 24/7 toegang tot het zelfhulpplatform praatuit.nl, periodieke awareness-mails, de Early Awareness Tool en een jaarverslag.',
        ],
      },
    ],
    summary: [
      'Een vertrouwenspersoon is op dit moment niet wettelijk verplicht. Het wetsvoorstel ligt nog bij de Eerste Kamer.',
      'Via de RI&E en de Arbeidsinspectie kan het aanstellen van een vertrouwenspersoon in de praktijk wel worden afgedwongen.',
      'Eén geëscaleerde situatie kost meer dan tien jaar abonnement bij een externe vertrouwenspersoon.',
      'Een extern abonnement bij PuntUit kost vanaf €375 per jaar — minder dan €40 per maand.',
    ],
    cta: 'Wil je weten wat PuntUit voor jouw organisatie kan betekenen?',
  },

  'interne-vs-externe-vertrouwenspersoon': {
    datePublished: '2026-03-18',
    dateModified: '2026-06-30',
    updatedLabel: 'juni 2026',
    intro: 'Veel organisaties twijfelen tussen een interne en een externe vertrouwenspersoon. Beide vormen zijn toegestaan en beide kunnen werken. Toch melden medewerkers in de praktijk liever bij iemand van buiten. Dit zijn de vijf belangrijkste redenen waarom een externe vertrouwenspersoon vaak effectiever is.',
    sections: [
      {
        heading: '1. Onafhankelijkheid zonder loyaliteitsconflict',
        body: [
          'Een interne vertrouwenspersoon is ook gewoon collega. Diegene kent de leidinggevende waarover het gaat, zit met dezelfde mensen in het kerstpakket en heeft een eigen positie te verliezen. Dat maakt volledige onpartijdigheid lastig, ook als de intenties goed zijn.',
          'Een externe vertrouwenspersoon staat helemaal buiten de organisatie. Geen hiërarchie, geen vriendschappen op de werkvloer, geen belang bij de uitkomst. Voor de melder voelt dat veiliger, en voor de organisatie is de onafhankelijkheid aantoonbaar.',
        ],
      },
      {
        heading: '2. Medewerkers durven eerder te melden',
        body: [
          'De grootste drempel bij ongewenst gedrag is de angst dat het bekend wordt. Bij een interne vertrouwenspersoon is die angst groter: "Straks weet de hele afdeling het." Die perceptie houdt mensen tegen, ook als de interne vertrouwenspersoon zich strikt aan de geheimhouding houdt.',
          'Bij een externe partij is de afstand groter en daarmee de drempel lager. Medewerkers melden eerder en eerder in het proces, waardoor situaties minder vaak escaleren.',
        ],
      },
      {
        heading: '3. Aantoonbare deskundigheid en bijscholing',
        body: [
          'Het vak van vertrouwenspersoon vraagt kennis van gespreksvoering, recht, opvang en doorverwijzing. Een interne collega doet de rol er vaak bij, naast het eigen werk, met beperkte tijd voor bijscholing.',
          'De vertrouwenspersonen van PuntUit zijn LVV-gecertificeerd en blijven continu bijgeschoold. Daardoor kunnen ze ook in complexe of juridisch gevoelige situaties professioneel handelen.',
        ],
      },
      {
        heading: '4. Continu bereikbaar, ook bij verzuim of vertrek',
        body: [
          'Een interne vertrouwenspersoon kan ziek worden, met vakantie zijn of de organisatie verlaten. Op zo\'n moment valt het meldpunt weg, precies wanneer een medewerker het nodig heeft.',
          'Bij een externe oplossing is de continuïteit geborgd. Medewerkers kunnen 24/7 terecht op het zelfhulpplatform praatuit.nl en zijn telefonisch en via WhatsApp bereikbaar op werkdagen van 9:00 tot 21:00.',
        ],
      },
      {
        heading: '5. Signaleren van patronen via rapportage',
        body: [
          'Eén melding zegt weinig, een patroon zegt alles. Een interne vertrouwenspersoon ziet vaak alleen losse gevallen en mag door de geheimhouding weinig delen.',
          'Een externe partij verzamelt signalen geanonimiseerd en levert een jaarlijkse rapportage. De werkgever ziet wat er speelt — zonder namen — en kan gericht beleid maken. De Early Awareness Tool van PuntUit signaleert patronen bovendien vroegtijdig.',
        ],
      },
    ],
    summary: [
      'Medewerkers melden eerder bij een externe vertrouwenspersoon doordat de drempel lager ligt.',
      'Een externe partij is aantoonbaar onafhankelijk en heeft geen loyaliteitsconflict.',
      'LVV-certificering en continue bijscholing borgen de deskundigheid.',
      'Continuïteit en geanonimiseerde rapportage zijn bij een externe oplossing geborgd.',
    ],
    cta: 'Twijfel je tussen intern en extern voor jouw organisatie?',
    related: {
      text: 'Wil je de twee vormen naast elkaar zien, inclusief kosten en wat de wet wel en niet verplicht?',
      label: 'Bekijk de vergelijking: interne vs. externe vertrouwenspersoon',
      to: '/interne-vs-externe-vp',
    },
  },

  'wat-kost-een-externe-vertrouwenspersoon': {
    datePublished: '2026-03-22',
    dateModified: '2026-06-30',
    updatedLabel: 'juni 2026',
    intro: 'Wat kost een externe vertrouwenspersoon? Het eerlijke antwoord: minder dan de meeste werkgevers denken, en veel minder dan de kosten van één geëscaleerde situatie. In dit artikel laten we zien hoe de prijs is opgebouwd, wat er wel en niet bij zit en hoe het zich verhoudt tot een interne oplossing.',
    sections: [
      {
        heading: 'Een vast jaarabonnement vanaf €375',
        body: [
          'Bij PuntUit werk je met een vast jaarabonnement. Voor organisaties tot en met 10 personen begint dat bij €375 exclusief BTW per jaar — minder dan €40 per maand. Voor 11 t/m 250 personen is het €475 per jaar; daarboven komt er per extra 250 personen €475 per jaar bij.',
          'Er zijn geen opstartkosten en het abonnement is jaarlijks opzegbaar. Je betaalt een voorspelbaar bedrag, ongeacht hoe vaak het spreekuur wordt gebruikt.',
        ],
      },
      {
        heading: 'Wat zit er in het abonnement?',
        body: [
          'Het abonnement is compleet: een externe LVV-gecertificeerde registervertrouwenspersoon, het gratis online spreekuur voor medewerkers, 24/7 toegang tot het zelfhulpplatform praatuit.nl, het PuntUit-spel om omgangsvormen bespreekbaar te maken, de Early Awareness Tool voor vroegsignalering, periodieke awareness-mails en een jaarlijks geanonimiseerd jaarverslag.',
          'Je hoeft zelf niets te regelen op het gebied van opleiding, certificering, onboarding of bereikbaarheid — dat zit allemaal in het abonnement.',
        ],
      },
      {
        heading: 'Wat valt buiten het abonnement?',
        body: [
          'Het online spreekuur en de eerste opvang vallen binnen het abonnement; in ruim 70% van de gevallen is dat voldoende. Is er verdere begeleiding nodig bij een concrete casus, dan geldt een uurtarief van €84.',
          'Gaat een vertrouwenspersoon op locatie aan de slag, dan wordt reistijd voor 50% van het uurtarief gerekend en geldt een kilometervergoeding van €0,25 per kilometer. Zo betaal je alleen voor extra inzet wanneer die daadwerkelijk nodig is.',
        ],
      },
      {
        heading: 'Afgezet tegen de kosten van niets doen',
        body: [
          'De grootste kostenpost is niet het abonnement, maar het ontbreken ervan. Eén situatie van langdurig verzuim, een arbeidsconflict of een juridische procedure loopt al snel in de tienduizenden euro\'s.',
          'Een laagdrempelig meldpunt voorkomt dat problemen escaleren. Vanuit dat oogpunt is een abonnement vanaf €375 per jaar geen kostenpost maar een verzekering tegen veel grotere schade.',
        ],
      },
    ],
    summary: [
      'Een jaarabonnement begint bij €375 excl. BTW (tot en met 10 personen); 11 t/m 250 personen €475 per jaar.',
      'Geen opstartkosten, jaarlijks opzegbaar en een vaste, voorspelbare prijs.',
      'Vertrouwenspersoon, online spreekuur, PuntUit-spel, praatuit.nl, Early Awareness Tool en jaarverslag zitten inbegrepen.',
      'Verdere begeleiding na het spreekuur kost €84 per uur.',
    ],
    cta: 'Benieuwd naar het tarief voor jouw organisatie?',
  },

  'ongewenst-gedrag-herkennen': {
    datePublished: '2026-03-28',
    dateModified: '2026-06-30',
    updatedLabel: 'juni 2026',
    intro: 'De meeste organisaties komen pas in actie als er een formele melding ligt. Dan is het probleem vaak al weken of maanden onderhuids aanwezig geweest. Ongewenst gedrag vroeg herkennen scheelt leed, verzuim en kosten. Zo doe je dat.',
    sections: [
      {
        heading: 'Wat valt onder ongewenst gedrag?',
        body: [
          'Ongewenst gedrag is een breed begrip. Het gaat om pesten, intimidatie, discriminatie, agressie en seksueel grensoverschrijdend gedrag, maar ook om subtielere vormen zoals structureel buitensluiten, kleineren of het systematisch negeren van iemand.',
          'Juist die subtiele vormen blijven vaak lang onzichtbaar. Ze laten geen duidelijk incident achter, maar tasten het werkplezier en de veiligheid net zo goed aan.',
        ],
      },
      {
        heading: 'De vroege signalen',
        body: [
          'Vroege signalen zitten zelden in één gebeurtenis. Ze zitten in patronen: een medewerker die zich terugtrekt, oplopend kortdurend verzuim, een team waar de sfeer merkbaar verandert, of iemand die opvallend vaak het mikpunt is van "grapjes".',
          'Leidinggevenden zien deze signalen niet altijd, omdat gedrag zich vaak verbergt op momenten dat er geen leiding bij is. Daarom is een onafhankelijk kanaal waar medewerkers anoniem hun zorg kunnen uiten zo waardevol.',
        ],
      },
      {
        heading: 'Waarom wachten op een melding te laat is',
        body: [
          'Tegen de tijd dat iemand een formele melding doet, is er meestal al veel gebeurd. De drempel om te melden is hoog, dus mensen wachten tot het echt niet meer gaat.',
          'Wie pas reageert op meldingen, reageert per definitie laat. Vroegsignalering verschuift het moment van ingrijpen naar voren, wanneer een situatie nog te keren is met een gesprek in plaats van een procedure.',
        ],
      },
      {
        heading: 'Vroegsignalering met de Early Awareness Tool',
        body: [
          'PuntUit gebruikt de Early Awareness Tool om gedragspatronen op het platform te analyseren. Het laat geen individuele meldingen zien, maar maakt geanonimiseerde trends zichtbaar: neemt de spanning binnen een afdeling toe, of komen bepaalde thema\'s opvallend vaak terug?',
          'Daarmee kan een werkgever ingrijpen voordat een situatie escaleert. Niet op basis van onderbuikgevoel, maar op basis van signalen die anders onzichtbaar zouden blijven.',
        ],
      },
      {
        heading: 'Wat je als werkgever zelf kunt doen',
        body: [
          'Maak het meldpunt zichtbaar en herhaal regelmatig dat het bestaat. Een vertrouwenspersoon die niemand kent, wordt niet gebruikt. Bespreek sociale veiligheid in teamoverleg, zodat het een normaal onderwerp wordt.',
          'En neem signalen serieus, ook de kleine. Een vroege, informele check kost vijf minuten. Een geëscaleerd conflict kost maanden.',
        ],
      },
    ],
    summary: [
      'Ongewenst gedrag begint vaak subtiel: buitensluiten, kleineren, oplopend kortdurend verzuim.',
      'Wie wacht op een formele melding, grijpt per definitie te laat in.',
      'De Early Awareness Tool maakt geanonimiseerde patronen zichtbaar vóór escalatie.',
      'Een zichtbaar, laagdrempelig meldpunt verlaagt de drempel om vroeg aan de bel te trekken.',
    ],
    cta: 'Wil je ongewenst gedrag vroeg signaleren in jouw organisatie?',
  },

  'wat-doet-een-vertrouwenspersoon': {
    datePublished: '2026-04-02',
    dateModified: '2026-06-30',
    updatedLabel: 'juni 2026',
    intro: 'Een vertrouwenspersoon is geen rechter, geen mediator en geen probleemoplosser. Toch is het misschien wel de belangrijkste schakel in een sociaal veilige organisatie. Wat doet een vertrouwenspersoon precies, en — minstens zo belangrijk — wat juist niet?',
    sections: [
      {
        heading: 'Luisteren en opvangen',
        body: [
          'De eerste taak van een vertrouwenspersoon is luisteren. Een medewerker die met ongewenst gedrag te maken heeft, wil zijn of haar verhaal in vertrouwen kwijt, zonder dat het meteen een officieel traject wordt.',
          'De vertrouwenspersoon biedt die veilige ruimte: vertrouwelijk, zonder oordeel en zonder dat er iets gebeurt waar de melder niet achter staat. Vaak is dat eerste gesprek al een grote opluchting.',
        ],
      },
      {
        heading: 'Begeleiden en adviseren',
        body: [
          'Na het luisteren helpt de vertrouwenspersoon de medewerker bij het overzien van de opties. Wat kun je doen? Wat zijn de gevolgen van elke stap? Wil je het informeel houden of een formele klacht indienen?',
          'De vertrouwenspersoon adviseert, maar beslist niet. De regie blijft altijd bij de medewerker. Wie alleen zijn verhaal kwijt wil en verder niets, houdt het daarbij.',
        ],
      },
      {
        heading: 'Doorverwijzen waar nodig',
        body: [
          'Soms vraagt een situatie om hulp die buiten de rol van vertrouwenspersoon valt — bijvoorbeeld een bedrijfsarts, mediator, jurist of psycholoog. De vertrouwenspersoon kent de weg en verwijst gericht door.',
          'Zo blijft de medewerker niet hangen, maar komt hij of zij bij de juiste persoon terecht voor de volgende stap.',
        ],
      },
      {
        heading: 'Signaleren richting de organisatie',
        body: [
          'Naast het individuele werk heeft de vertrouwenspersoon een signalerende functie. Door geanonimiseerd patronen te delen, helpt de vertrouwenspersoon de organisatie om structurele problemen aan te pakken.',
          'Bij PuntUit gebeurt dit via een jaarlijkse geanonimiseerde rapportage: de werkgever ziet wat er speelt, zonder dat individuele gevallen herleidbaar zijn.',
        ],
      },
      {
        heading: 'Wat een vertrouwenspersoon niet doet',
        body: [
          'Een vertrouwenspersoon lost het probleem niet zelf op. Hij of zij velt geen oordeel over schuld, doet geen onderzoek en spreekt de beschuldigde niet aan. Dat zijn taken voor andere rollen, zoals een klachtencommissie of onderzoeker.',
          'Die afbakening is bewust. Juist doordat de vertrouwenspersoon volledig aan de kant van de melder staat en niets doet zonder diens instemming, durven mensen zich te melden.',
        ],
      },
    ],
    summary: [
      'Een vertrouwenspersoon luistert, vangt op, begeleidt en adviseert — vertrouwelijk en zonder oordeel.',
      'De regie blijft altijd bij de medewerker; de vertrouwenspersoon beslist niets.',
      'Waar nodig wordt gericht doorverwezen naar bijvoorbeeld een bedrijfsarts of mediator.',
      'Een vertrouwenspersoon doet geen onderzoek en velt geen oordeel — dat is juist de kracht.',
    ],
    cta: 'Wil je weten hoe een vertrouwenspersoon binnen jouw organisatie werkt?',
  },

  'verschil-vertrouwenspersoon-mediator': {
    datePublished: '2026-04-08',
    dateModified: '2026-06-30',
    updatedLabel: 'juni 2026',
    intro: 'Een vertrouwenspersoon en een mediator worden vaak met elkaar verward. Beide spelen een rol bij spanningen op de werkvloer, maar ze doen iets heel anders. Wie de rollen door elkaar haalt, zet de verkeerde persoon op de verkeerde situatie. Een heldere uitleg.',
    sections: [
      {
        heading: 'De vertrouwenspersoon: aan de kant van één persoon',
        body: [
          'Een vertrouwenspersoon staat volledig aan de kant van de melder. Diegene luistert, vangt op, adviseert en begeleidt — vertrouwelijk en zonder oordeel. Het belang van de melder staat centraal.',
          'De vertrouwenspersoon is dus nadrukkelijk niet neutraal tussen partijen. Dat hoort ook niet: de rol bestaat juist om iemand die zich onveilig voelt een veilige plek te bieden.',
        ],
      },
      {
        heading: 'De mediator: tussen twee partijen in',
        body: [
          'Een mediator doet het tegenovergestelde. Die is juist wél neutraal en begeleidt twee of meer partijen die samen een conflict willen oplossen. De mediator kiest geen kant en stuurt op een oplossing waar beide partijen mee verder kunnen.',
          'Mediation veronderstelt dat beide partijen bereid zijn om met elkaar in gesprek te gaan en samen tot een uitkomst te komen. Dat is een wezenlijk andere situatie dan iemand die zich in vertrouwen wil uiten.',
        ],
      },
      {
        heading: 'Wanneer zet je wie in?',
        body: [
          'Begin bij een vertrouwenspersoon wanneer een medewerker met ongewenst gedrag te maken heeft, zich onveilig voelt of gewoon zijn verhaal in vertrouwen kwijt wil. Er is dan nog geen sprake van een onderhandeling, maar van opvang en advies.',
          'Schakel een mediator in wanneer er een concreet conflict ligt tussen twee partijen die er samen uit willen komen. In de praktijk kan een traject bij de vertrouwenspersoon beginnen en later, met instemming van de medewerker, uitmonden in mediation.',
        ],
      },
      {
        heading: 'Waarom het onderscheid ertoe doet',
        body: [
          'Iemand die zich onveilig voelt naar een mediationtafel sturen, kan averechts werken: de melder voelt zich dan onder druk gezet om "het samen op te lossen" met degene die de onveiligheid veroorzaakt.',
          'Andersom kan een vertrouwenspersoon een zakelijk meningsverschil tussen gelijkwaardige collega\'s niet beslechten. De juiste rol op het juiste moment voorkomt dat een situatie verkeerd wordt aangepakt.',
        ],
      },
    ],
    summary: [
      'Een vertrouwenspersoon staat aan de kant van de melder; een mediator is neutraal tussen partijen.',
      'De vertrouwenspersoon vangt op en adviseert; de mediator begeleidt naar een gezamenlijke oplossing.',
      'Bij onveiligheid of ongewenst gedrag begin je bij de vertrouwenspersoon, niet bij mediation.',
      'Een traject kan bij de vertrouwenspersoon starten en later, met instemming, overgaan in mediation.',
    ],
    cta: 'Niet zeker welke rol jouw situatie vraagt?',
  },
};

export function Article() {
  const { slug } = useParams<{ slug: string }>();
  return <ArticlePage key={slug} slug={slug} />;
}

function ArticlePage({ slug }: { slug: string | undefined }) {
  const staticMeta = ARTICLES.find((article) => article.slug === slug);
  const staticContent = slug ? ARTICLE_CONTENT[slug] : undefined;
  const hasStatic = Boolean(staticMeta && staticContent);
  const prerendered = hasStatic ? null : getMatchingPrerenderedArticle(slug);
  const [remote, setRemote] = useState<(Article & ArticleBody) | null>(prerendered);
  const [remoteReady, setRemoteReady] = useState(hasStatic || Boolean(prerendered));

  useEffect(() => {
    if (!slug || hasStatic) return;

    let cancelled = false;
    fetchApiArticle(slug).then((article) => {
      if (cancelled) return;
      setRemote((current) => article ?? current);
      setRemoteReady(true);
    });

    return () => {
      cancelled = true;
    };
  }, [slug, hasStatic]);

  if (staticMeta && staticContent && slug) {
    return (
      <ArticleView
        slug={slug}
        meta={staticMeta}
        content={staticContent}
        nextArticle={nextStaticArticle(slug)}
      />
    );
  }

  if (!remoteReady) {
    return <ArticleLoading />;
  }

  if (remote && slug) {
    return (
      <ArticleView
        slug={slug}
        meta={remote}
        content={remote}
        nextArticle={nextArticleFromContent(remote) ?? ARTICLES[0]}
      />
    );
  }

  return <Navigate to="/kennisbank" replace />;
}

function nextStaticArticle(slug: string): Article {
  const currentIndex = ARTICLES.findIndex((article) => article.slug === slug);
  return ARTICLES[currentIndex + 1] || ARTICLES[0];
}

function nextArticleFromContent(content: ArticleBody): Pick<Article, 'slug' | 'title'> | undefined {
  if (content.nextArticle) {
    return { slug: content.nextArticle.slug, title: content.nextArticle.title };
  }

  if (content.related?.to.startsWith('/kennisbank/')) {
    return {
      slug: content.related.to.replace('/kennisbank/', ''),
      title: content.related.label,
    };
  }

  return undefined;
}

function ArticleLoading() {
  return (
    <div className="pt-28">
      <section className="bg-[#0284c7] text-white">
        <div className="container py-16 md:py-20 max-w-4xl">
          <p className="text-white/70">Artikel laden...</p>
        </div>
      </section>
    </div>
  );
}

function ArticleView({
  slug,
  meta,
  content,
  nextArticle,
}: {
  slug: string;
  meta: Article;
  content: ArticleBody;
  nextArticle: Pick<Article, 'slug' | 'title'> & Partial<Pick<Article, 'tag'>>;
}) {
  return (
    <>
      <SEO title={meta.title} description={meta.desc} canonicalUrl={`/kennisbank/${slug}`} ogType="article" />
      <SchemaMarkup
        type="article"
        articleMeta={{
          headline: meta.title,
          description: meta.desc,
          datePublished: content.datePublished,
          dateModified: content.dateModified,
          slug: slug!,
        }}
        breadcrumb={[
          { name: 'Home', url: '/' },
          { name: 'Kennisbank', url: '/kennisbank' },
          { name: meta.title, url: `/kennisbank/${slug}` },
        ]}
      />
      <div className="pt-28">

        <section className="bg-[#0284c7] text-white">
          <div className="container py-16 md:py-20 max-w-4xl">
            <Link to="/kennisbank" className="inline-flex items-center gap-1.5 text-white/60 hover:text-white text-sm mb-8 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Terug naar kennisbank
            </Link>
            <div className="flex items-center gap-3 mb-6">
              <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest bg-white/10 text-white/80 px-3 py-1.5 rounded-md">
                <Tag className="w-3 h-3" />
                {meta.tag}
              </span>
              <span className="flex items-center gap-1 text-xs text-white/50">
                <Clock className="w-3 h-3" />
                {meta.readTime} min leestijd
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-4">{meta.title}</h1>
            <p className="text-xs text-white/50">Laatst bijgewerkt: {content.updatedLabel}</p>
          </div>
        </section>

        <section className="section-padding bg-white">
          <div className="container max-w-3xl">
            <p className="text-lg text-gray-700 leading-relaxed mb-10 font-medium">
              {content.intro}
            </p>

            <div className="space-y-10">
              {content.sections.map((section, sectionIndex) => (
                <div key={section.heading || sectionIndex}>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">{section.heading}</h2>
                  {(section.body ?? []).map((paragraph, i) => (
                    <p key={i} className="text-gray-600 leading-relaxed mb-4">{paragraph}</p>
                  ))}
                </div>
              ))}
            </div>

            {content.related && !content.nextArticle && (
              <div className="mt-10 bg-sky-50 rounded-2xl p-6 border border-sky-100">
                <p className="text-gray-700 mb-2">{content.related.text}</p>
                <Link to={content.related.to} className="inline-flex items-center gap-1.5 text-[#0284c7] font-semibold hover:gap-2.5 transition-all">
                  {content.related.label} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            )}

            <div className="mt-14 bg-[#0284c7] rounded-2xl p-8 md:p-10 text-white">
              <h3 className="text-2xl font-bold mb-3">{content.cta}</h3>
              <p className="text-white/80 mb-6">{content.ctaText ?? 'Meer dan 500 organisaties gingen je voor. Plan vandaag een kennismaking.'}</p>
              <div className="flex flex-wrap items-center gap-4">
                <Link to="/contact" className="btn-primary">
                  Plan een kennismaking <ArrowRight className="w-4 h-4" />
                </Link>
                <BrochureButton variant="white" />
              </div>
            </div>

            <div className="mt-14 border-t border-gray-200 pt-10">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-400 mb-2">Volgende artikel</p>
              <Link
                to={`/kennisbank/${nextArticle.slug}`}
                className="group flex items-center justify-between gap-4 p-5 rounded-xl border border-gray-100 hover:shadow-md transition-all"
              >
                <div>
                  {nextArticle.tag && (
                    <span className="inline-block text-[10px] font-bold uppercase tracking-widest bg-[#0284c7]/5 text-[#0284c7] px-2.5 py-1 rounded-md mb-2">{nextArticle.tag}</span>
                  )}
                  <h4 className="font-semibold text-gray-900 group-hover:text-[#0284c7] transition-colors">{nextArticle.title}</h4>
                </div>
                <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-[#0284c7] flex-shrink-0 transition-colors" />
              </Link>
            </div>

            <div className="mt-14 bg-gray-50 rounded-2xl p-8 border border-gray-100">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Samenvatting</h3>
              <ul className="space-y-3">
                {content.summary.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-600 text-sm">
                    <CheckCircle className="w-4 h-4 text-[#0284c7] mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

      </div>
    </>
  );
}
